import React, { useEffect } from 'react';
import * as PropTypes from 'prop-types';
import {
  Row, Typography, Spin, Button, Icon, Skeleton, Col,
} from 'antd';
import { FormattedMessage } from 'react-intl';
import { withRouter } from 'react-router-dom';
import '../Configure.css';
import { connect } from 'react-redux';
import ConfigHeading from '../components/ConfigHeading';
import { userIsAdmin } from './user-util';
import { requestUserGroupDetails } from '../../../../state/ui/configuration/company/companyUiActions';
import { selectFetchUserGroupRequest } from '../../../../state/ui/configuration/company/companyUiReducer';
import { selectUserGroup } from '../../../../state/domain/company/companyReducer';
import { configRoutes } from '../../../../utility/route-locations';
import { selectSessionUser } from '../../../../state/ui/application/sessionUiReducer';
import './ContactDetails.css';
import { requestSupportedLanguages } from '../../../../state/ui/application/sessionUiActions';
import { selectAppConfig } from '../../../../state/domain/appConfig/appConfigReducer';
import DisplayAddress from '../../../../components/fields/DisplayAddress';

const { Text } = Typography;


/**
 * Displays the logo for a user group.
 * @param {object} props The react props
 * @param {object} props.userGroup The user group being edited.
 */
function LogoField({ userGroup }) {
  // User group has not loaded yet.
  if (!userGroup) return <Row type="flex" align="middle"><Spin size="large" style={{ marginLeft: '27px', marginBottom: '10px' }} /></Row>;

  // There is no logo defined.
  if (!userGroup.logo || !userGroup.logoType) {
    return (
      <div className="via-fake-upload via-fake-upload--no-edit" style={{ padding: '10px' }}>
        <Row>
          <Col span={24}>
            <Icon type="exclamation-circle" style={{ fontSize: '30px', marginTop: '10px' }} />
          </Col>
          <Col span={24}>
            <Text style={{ marginBottom: '20px' }}><FormattedMessage id="noLogoUploaded" /></Text>
          </Col>
        </Row>
      </div>
    );
  }

  // Show the logo as is.
  return <img src={`data:${userGroup.logoType};base64,${userGroup.logo}`} alt="" className="via-fake-upload" />;
}

LogoField.propTypes = {
  userGroup: PropTypes.shape({ logo: PropTypes.string.isRequired, logoType: PropTypes.string.isRequired }),
};

LogoField.defaultProps = {
  userGroup: undefined,
};


/**
 * The display screen for the company configuration data.
 * @param {object} props The react props.
 */
function ContactDetails({
  languages, getAvailiableLanguages, loadPageData, userGroup, history, sessionUser,
}) {
  useEffect(() => {
    if (!userGroup) loadPageData();
    if (!languages) getAvailiableLanguages();
  }, [loadPageData, userGroup, languages, getAvailiableLanguages]);

  return (
    <ConfigHeading
      titleId="contactDetails"
      descriptionId="contactDetailsDesc"
      actions={userIsAdmin(sessionUser) ? (
        <Button type="primary" icon="edit" loading={!userGroup} onClick={() => history.push(configRoutes.configure_edit_contact_details)}>
          <FormattedMessage id="edit" />
        </Button>
      ) : null}
    >
      <Row>
        <Row className="contact-details-content-row">
          <Row style={{ marginBottom: '12px' }}>
            <Text strong><FormattedMessage id="companyName" /></Text>
          </Row>
          <Row>
            <Text>{userGroup ? userGroup.description : <Skeleton title={false} paragraph={{ width: 200, rows: 1 }} />}</Text>
          </Row>
        </Row>
        <Row className="contact-details-content-row">
          <Row style={{ marginBottom: '12px' }}>
            <Text strong><FormattedMessage id="taxRefNo" /></Text>
          </Row>
          <Row>
            <Text>{userGroup ? userGroup.taxReferenceNo || <FormattedMessage id="notProvided" /> : <Skeleton title={false} paragraph={{ width: 200, rows: 1 }} />}</Text>
          </Row>
        </Row>
        <Row className="contact-details-content-row">
          <Row style={{ marginBottom: '12px' }}>
            <Text strong><FormattedMessage id="companyLogo" /></Text>
          </Row>
          <Row>
            <LogoField userGroup={userGroup} />
          </Row>
        </Row>
        <Row className="contact-details-content-row">
          <Row style={{ marginBottom: '12px' }}>
            <Text strong><FormattedMessage id="companyAddress" /></Text>
          </Row>
          <Row>
            {userGroup
              ? <DisplayAddress address={userGroup.address} withLineBreaks />
              : <Skeleton title={false} paragraph={{ width: [200, 200, 200, 200, 100, 100, 100], rows: 7 }} />}
          </Row>
        </Row>
        <Row className="contact-details-content-row">
          <Row style={{ marginBottom: '12px' }}>
            <Text strong><FormattedMessage id="contactByEmail" /></Text>
          </Row>
          <Row>
            <Text>{userGroup ? userGroup.contactEmail || <FormattedMessage id="notProvided" /> : <Skeleton title={false} paragraph={{ width: 200, rows: 1 }} />}</Text>
          </Row>
        </Row>
        <Row className="contact-details-content-row">
          <Row style={{ marginBottom: '12px' }}>
            <Text strong><FormattedMessage id="contactByPhone" /></Text>
          </Row>
          <Row>
            <Text>{userGroup ? userGroup.contactPhone || <FormattedMessage id="notProvided" /> : <Skeleton title={false} paragraph={{ width: 200, rows: 1 }} />}</Text>
          </Row>
        </Row>
        <Row className="contact-details-content-row">
          <Row style={{ marginBottom: '12px' }}>
            <Text strong><FormattedMessage id="preferredCurrency" /></Text>
          </Row>
          <Row>
            <Text>
              {userGroup && userGroup.currency
                ? <>{`${userGroup.currency.currencyCode} - ${userGroup.currency.displayCode}`}</>
                : <Skeleton title={false} paragraph={{ width: 200, rows: 1 }} />}
            </Text>
          </Row>
        </Row>
        <Row className="contact-details-content-row">
          <Row style={{ marginBottom: '12px' }}>
            <Text strong><FormattedMessage id="preferredLanguage" /></Text>
          </Row>
          <Row>
            <Text>
              {userGroup && languages
                ? languages.find(language => language.languageCode === userGroup.defaultLocale).languageDescription
                : <Skeleton title={false} paragraph={{ width: 200, rows: 1 }} />}
            </Text>
          </Row>
        </Row>
      </Row>
    </ConfigHeading>
  );
}


ContactDetails.propTypes = {
  loadPageData: PropTypes.func.isRequired,
  userGroup: PropTypes.shape({
    description: PropTypes.string.isRequired,
    taxReferenceNo: PropTypes.string,
    defaultLocale: PropTypes.string.isRequired,
    address: PropTypes.shape({
      line1: PropTypes.string.isRequired,
      line2: PropTypes.string,
      line3: PropTypes.string,
      line4: PropTypes.string,
      region: PropTypes.string,
      zipPostcode: PropTypes.string,
      country: PropTypes.string.isRequired,
    }),
    contactEmail: PropTypes.string,
    contactPhone: PropTypes.string,
    currency: PropTypes.shape({
      currencyCode: PropTypes.string,
      displayCode: PropTypes.string,
    }),
    logo: PropTypes.string,
    logoType: PropTypes.string,
  }),
  sessionUser: PropTypes.shape({
    linkedRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  history:
    PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
  languages: PropTypes.arrayOf(PropTypes.shape({
    languageCode: PropTypes.string.isRequired,
    languageDescription: PropTypes.string.isRequired,
  })),
  getAvailiableLanguages: PropTypes.func.isRequired,
};

ContactDetails.defaultProps = {
  userGroup: null,
  languages: null,
};

/**
 * A function for connecting redux state to the component props.
 */
const mapStateToProps = (state) => {
  const pageDataRequest = selectFetchUserGroupRequest(state);

  return {
    error: pageDataRequest ? pageDataRequest.error : null,
    userGroup: selectUserGroup(state),
    sessionUser: selectSessionUser(state),
    languages: selectAppConfig(state, 'languages') ? selectAppConfig(state, 'languages').languages : null,
  };
};

/**
 * A function for connecting redux action to the component props.
 */
const mapDispatchToProps = dispatch => ({
  loadPageData: () => dispatch(requestUserGroupDetails()),
  getAvailiableLanguages: () => dispatch(requestSupportedLanguages()),
});

/**
 * Connect the redux state to our component.
 */
export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ContactDetails));
